import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, FileText, CalendarClock, BellRing, LogOut, Building2, Inbox, Settings2, Wrench, Menu, X, CalendarDays, FolderKanban, MessageSquare, Sparkles, HardHat, Receipt } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const LOGO_URL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_infra-manager-15/artifacts/9lvbt023_Logo%20only%20kayser%20Kopie.png";

const items = [
  // Startseite: rollenspezifisch
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", hideForAuftraggeber: true, hideForSachbearbeiter: true, hideForMonteur: true },
  { to: "/", label: "Meine Projekte", icon: Sparkles, testid: "nav-mein-dashboard", onlyForAuftraggeber: true },
  { to: "/", label: "Mein Tag", icon: HardHat, testid: "nav-monteur-portal", onlyForMonteur: true },
  { to: "/mein-dashboard", label: "Kunden-Cockpit", icon: Sparkles, testid: "nav-kunden-cockpit", adminOnly: true },
  { to: "/anmeldungen", label: "Anmeldungen", icon: FileText, testid: "nav-anmeldungen", hideForMonteur: true },
  { to: "/projekte", label: "Projekte", icon: Wrench, testid: "nav-projekte", hideForSachbearbeiter: true, hideForMonteur: true },
  { to: "/rechnungen", label: "Rechnungen", icon: Receipt, testid: "nav-rechnungen", strictAdmin: true },
  { to: "/kunden", label: "Kunden", icon: FolderKanban, testid: "nav-kunden", hideForAuftraggeber: true, hideForSachbearbeiter: true, hideForMonteur: true },
  { to: "/projekttermine", label: "Projekttermine", icon: CalendarDays, testid: "nav-projekttermine", hideForSachbearbeiter: true },
  { to: "/zaehlertermine", label: "Zählertermine", icon: CalendarClock, testid: "nav-zaehlertermine", hideForSachbearbeiter: true, hideForMonteur: true },
  { to: "/nachrichten", label: "Nachrichten", icon: MessageSquare, testid: "nav-nachrichten", badgeKey: "msg_unread" },
  { to: "/posteingang", label: "Posteingang", icon: Inbox, testid: "nav-posteingang", badgeKey: "unseen", adminOnly: true },
  { to: "/erinnerungen", label: "Erinnerungen", icon: BellRing, testid: "nav-erinnerungen", adminOnly: true },
];

export function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [inboxCount, setInboxCount] = useState({ unseen: 0, unmatched: 0, msg_unread: 0 });
  const [mobileOpen, setMobileOpen] = useState(false);

  const canSeeInbox = ["admin", "innendienst"].includes(user?.role);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [inbox, msg] = await Promise.all([
          canSeeInbox
            ? api.get("/email/inbox-count").then((x) => x.data).catch(() => ({ unseen: 0, unmatched: 0 }))
            : Promise.resolve({ unseen: 0, unmatched: 0 }),
          api.get("/messages/unread-count").then((x) => x.data).catch(() => ({ unread: 0 })),
        ]);
        if (alive) setInboxCount({ ...inbox, msg_unread: msg.unread || 0 });
      } catch {}
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, [canSeeInbox]);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const closeMobile = () => setMobileOpen(false);

  const NavBody = (
    <>
      <div className="px-6 py-6 border-b border-border flex items-center justify-between">
        <div>
          <img src={LOGO_URL} alt="KAYSER" className="h-8 w-auto object-contain" data-testid="brand-logo" />
        </div>
        <button className="lg:hidden text-muted-foreground" onClick={closeMobile} data-testid="mobile-close"><X className="h-5 w-5" /></button>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {items.filter((it) => (
          (!it.adminOnly || ["admin","innendienst"].includes(user?.role))
          && !(it.strictAdmin && user?.role !== "admin")
          && !(it.hideForAuftraggeber && user?.role === "auftraggeber")
          && !(it.hideForSachbearbeiter && user?.role === "sachbearbeiter")
          && !(it.hideForMonteur && user?.role === "monteur")
          && !(it.onlyForAuftraggeber && user?.role !== "auftraggeber")
          && !(it.onlyForMonteur && user?.role !== "monteur")
        )).map(({ to, label, icon: Icon, testid, badgeKey }) => {
          const count = badgeKey ? inboxCount[badgeKey] : 0;
          return (
            <NavLink key={to} to={to} end={to === "/"} data-testid={testid} onClick={closeMobile}
              className={({ isActive }) =>
                `flex items-center justify-between gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ` +
                (isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground")
              }
            >
              <span className="flex items-center gap-3"><Icon className="h-4 w-4" />{label}</span>
              {count > 0 && (
                <span className="rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 min-w-[1.25rem] h-5 inline-flex items-center justify-center" data-testid={`${testid}-badge`}>{count}</span>
              )}
            </NavLink>
          );
        })}
        {["admin","innendienst"].includes(user?.role) && (
          <div className="pt-4 mt-2 border-t border-border">
            <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Verwaltung</div>
            <NavLink to="/einstellungen" data-testid="nav-einstellungen" onClick={closeMobile}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ` +
                (isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground")
              }>
              <Settings2 className="h-4 w-4" /> Einstellungen
            </NavLink>
          </div>
        )}
      </nav>
      <div className="p-3 border-t border-border">
        <div className="px-3 py-2 mb-2">
          <div className="text-xs text-muted-foreground">Angemeldet als</div>
          <div className="text-sm font-medium truncate" data-testid="current-user-email">{user?.email}</div>
          {user?.role && user.role !== "user" && (
            <div className="text-[10px] uppercase tracking-wider text-primary mt-0.5">{user.role}</div>
          )}
        </div>
        <Button variant="outline" size="sm" className="w-full justify-start" onClick={handleLogout} data-testid="logout-button">
          <LogOut className="h-4 w-4 mr-2" /> Abmelden
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-card border-b border-border h-14 flex items-center px-4 gap-3" data-testid="mobile-topbar">
        <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-open"><Menu className="h-5 w-5" /></button>
        <img src={LOGO_URL} alt="KAYSER" className="h-6 w-auto object-contain" />
      </div>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card min-h-screen flex-col" data-testid="sidebar">
        {NavBody}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={closeMobile} data-testid="mobile-backdrop" />
          <aside className="fixed left-0 top-0 bottom-0 w-64 z-50 bg-card border-r border-border flex flex-col lg:hidden" data-testid="mobile-sidebar">
            {NavBody}
          </aside>
        </>
      )}
    </>
  );
}
